import { useEffect, useState } from 'react';
import { TaxRateSelect } from '../components/TaxRateSelect.tsx';
import { knownRateOr } from '../lib/taxes.ts';
import { Link, useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, type Tone } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { dateTime, day, money, parseMoney } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useUi } from '../lib/ui-state.tsx';
import { blankLine, byRate, customerHint, lineFigures, parseNumber, priceText, qtyText, Totals, UNITS, type Customer, type EditLine, type Sellable, type Unit } from './Invoices.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { Bars, downloadCsv, Kpi, Table } from './Reports.tsx';

/**
 * Subscriptions: customers who pay the same again every week, month, quarter or year. The page
 * shows what that brings in each month and how it moves, who is billed next and who is behind,
 * every subscription with its invoices, the plans they start from and the months gone by.
 */

type State = 'draft' | 'active' | 'paused' | 'closed';
type PeriodUnit = 'week' | 'month' | 'year';
type Reason = 'price' | 'not_using' | 'competitor' | 'business_closed' | 'unpaid' | 'ended' | 'other';
type Filter = 'running' | 'active' | 'paused' | 'draft' | 'ending' | 'closed' | 'all';
type Tab = 'overview' | 'list' | 'plans' | 'results';
const REASONS: Reason[] = ['price', 'not_using', 'competitor', 'business_closed', 'unpaid', 'other'];

interface SubLine {
  product: string | null;
  name: string;
  unit: Unit;
  qty: number;
  unit_price: number;
  discount: number;
  tax_rate: number;
}
interface Row {
  id: string;
  number: number;
  state: State;
  customer: string;
  customer_name: string;
  customer_phone: string | null;
  plan: string | null;
  plan_name: string | null;
  every: number;
  unit: PeriodUnit;
  lines: SubLine[];
  net: number;
  tax: number;
  total: number;
  mrr: number;
  arr: number;
  start_date: string;
  next_invoice: string;
  end_date: string | null;
  auto_issue: boolean;
  reference: string | null;
  note: string | null;
  started_at: string | null;
  paused_at: string | null;
  closed_at: string | null;
  close_reason: Reason | 'ended' | null;
  close_note: string | null;
  current: { start: string; end: string } | null;
  ending: boolean;
  due: boolean;
}
interface HistoryRow {
  id: string;
  kind: 'started' | 'changed' | 'paused' | 'resumed' | 'closed' | 'invoiced' | 'held';
  mrr_before: number;
  mrr_after: number;
  invoice: string | null;
  invoice_name: string | null;
  invoice_state: 'draft' | 'issued' | 'paid' | 'cancelled' | null;
  invoice_total: number | null;
  invoice_paid: number | null;
  invoice_due: string | null;
  period_start: string | null;
  period_end: string | null;
  note: string | null;
  at: string;
  by_name: string | null;
}
interface Detail extends Row {
  history: HistoryRow[];
  upcoming: { start: string; end: string }[];
  billed: number;
  open: number;
}
interface Movement {
  new: number;
  expansion: number;
  contraction: number;
  churn: number;
  paused: number;
  resumed: number;
  net: number;
  started: number;
  stopped: number;
}
interface Overview {
  today: string;
  mrr: number;
  arr: number;
  active: number;
  paused: number;
  drafts: number;
  ending: number;
  customers: number;
  arpa: number;
  month: Movement;
  due: Row[];
  owing: { id: string; name: string; overdue: number; oldest: string; invoices: number }[];
  held: number;
  trend: { day: string; mrr: number; active: number }[];
}
interface Plan {
  id: string;
  name: string;
  every: number;
  unit: PeriodUnit;
  lines: SubLine[];
  auto_issue: boolean;
  description: string | null;
  sequence: number;
  active: boolean;
  net?: number;
  total?: number;
  mrr?: number;
  subscribers?: number;
}
interface Report {
  months: (Movement & { month: string; mrr: number; active: number; churn_rate: number })[];
  reasons: { reason: Reason | null; n: number; mrr: number }[];
  plans: { plan: string | null; active: number; mrr: number }[];
}

const TABS: { key: Tab; icon: IconName }[] = [
  { key: 'overview', icon: 'chart' },
  { key: 'list', icon: 'list' },
  { key: 'plans', icon: 'layers' },
  { key: 'results', icon: 'trendUp' },
];
const STATE: Record<State, { tone: Tone; icon: IconName }> = {
  draft: { tone: 'neutral', icon: 'edit' },
  active: { tone: 'ok', icon: 'checkCircle' },
  paused: { tone: 'warn', icon: 'pause' },
  closed: { tone: 'neutral', icon: 'x' },
};
export const subName = (n: number) => `S-${n}`;
/** Every month labelled when there are few; every third, counted back from the last, when there are many. */
const sparse = (i: number, n: number, label: string) => (n <= 8 || (n - 1 - i) % 3 === 0 ? label : '');
const monthName = (ym: string) => new Intl.DateTimeFormat(undefined, { month: 'short', year: '2-digit', timeZone: 'UTC' }).format(new Date(`${ym}-15T00:00:00Z`));

/** "Every month", "Every 3 months", "Every year". */
export function howOften(every: number, unit: PeriodUnit) {
  return every === 1 ? t(`sub.every_${unit}` as MessageKey) : t(`sub.every_n_${unit}` as MessageKey, { n: every });
}
function perPeriod(every: number, unit: PeriodUnit) {
  return every === 1 ? t(`sub.per_${unit}` as MessageKey) : howOften(every, unit).toLowerCase();
}
const toEdit = (l: SubLine): EditLine => ({ product: l.product ?? '', name: l.name, unit: l.unit, qty: qtyText(l.qty), price: priceText(l.unit_price), discount: l.discount ? String(l.discount) : '', vat: l.tax_rate });
const monthlyOf = (net: number, every: number, unit: PeriodUnit) => Math.round(unit === 'month' ? net / every : unit === 'year' ? net / (12 * every) : (net * 52) / (12 * every));

function SubBadge({ s }: { s: Pick<Row, 'state' | 'ending' | 'due'> }) {
  if (s.state === 'active' && s.ending) return <StatusBadge tone="warn" icon="clock" label={t('sub.st_ending')} />;
  const x = STATE[s.state];
  return <StatusBadge tone={x.tone} icon={x.icon} label={t(`sub.st_${s.state}` as MessageKey)} />;
}

export function Subscriptions() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const { busy, run } = useSave();
  const tab = (TABS.some((x) => x.key === params.get('tab')) ? params.get('tab') : 'overview') as Tab;
  const { data: o, reload } = useAction<Overview>('recurring.overview');
  const [version, setVersion] = useState(0);
  const [form, setForm] = useState<null | { id?: string; plan?: string; customer?: string }>(null);
  const open = params.get('sub');
  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) (v === null ? p.delete(k) : p.set(k, v));
    setParams(p, { replace: true });
  };
  const changed = async () => {
    setVersion((v) => v + 1);
    await reload();
  };
  const show = (id: string | null) => set({ sub: id });
  const manage = can('recurring.manage');
  const billNow = () =>
    void run(async () => {
      const r = await action<{ invoices: number; held: number; ended: number }>('recurring.run');
      await changed();
      return r;
    }, t('sub.run_done'));
  const net = o?.month.net ?? 0;

  return (
    <Shell>
      <div className="page reports sub-page">
        <div className="page-head">
          <div>
            <h1>{t('sub.title')}</h1>
            <p className="muted">{t('sub.subtitle')}</p>
          </div>
          {manage ? (
            <div className="page-actions">
              <Button icon="invoice" busy={busy} onClick={billNow} disabled={!o || !o.due.some((d) => d.due)}>
                {t('sub.run')}
              </Button>
              <Button variant="primary" icon="plus" onClick={() => setForm({})}>
                {t('sub.new')}
              </Button>
            </div>
          ) : null}
        </div>
        {!o ? (
          <Skeleton h={110} r="var(--radius-lg)" />
        ) : (
          <section className="rep-kpis" aria-label={t('sub.at_a_glance')}>
            <Kpi label={t('sub.k_mrr')} value={money(o.mrr)} hint={t('sub.k_arr', { amount: money(o.arr) })} />
            <Kpi label={t('sub.k_active')} value={String(o.active)} hint={[tn('sub.k_customers', o.customers), o.paused ? tn('sub.k_paused', o.paused) : null].filter(Boolean).join(' · ')} />
            <Kpi label={t('sub.k_change')} value={`${net > 0 ? '+' : net < 0 ? '−' : ''}${money(Math.abs(net))}`} hint={(() => {
                const added = o.month.new + o.month.expansion + o.month.resumed;
                const lost = o.month.churn + o.month.contraction + o.month.paused;
                return lost ? t('sub.k_change_hint', { added: money(added), lost: money(lost) }) : t('sub.k_change_nothing_lost');
              })()} warn={net < 0} />
            <Kpi label={t('sub.k_arpa')} value={money(o.arpa)} hint={o.held ? tn('sub.k_held', o.held) : t('sub.k_arpa_hint')} warn={o.held > 0} />
          </section>
        )}

        <div className="segmented tabs sub-tabs" role="tablist" aria-label={t('sub.sections')}>
          {TABS.map((x) => (
            <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => set({ tab: x.key === 'overview' ? null : x.key, f: null })}>
              <Icon name={x.icon} size={16} />
              {t(`sub.tab_${x.key}` as MessageKey)}
            </button>
          ))}
        </div>

        {!o ? null : tab === 'overview' ? (
          <OverviewTab o={o} onOpen={show} />
        ) : tab === 'list' ? (
          <ListTab key={version} params={params} set={set} onOpen={show} />
        ) : tab === 'plans' ? (
          <PlansTab onUse={manage ? (plan) => setForm({ plan }) : undefined} />
        ) : (
          <ResultsTab key={version} />
        )}
      </div>
      {open ? <SubDialog key={`${open}-${version}`} id={open} onClose={() => show(null)} onChange={changed} onEdit={(id) => setForm({ id })} /> : null}
      {form ? (
        <SubForm
          id={form.id}
          plan={form.plan}
          onClose={() => setForm(null)}
          onSaved={async (id) => {
            setForm(null);
            await changed();
            show(id);
          }}
        />
      ) : null}
    </Shell>
  );
}

// ---- overview ---------------------------------------------------------------------------------

function OverviewTab({ o, onOpen }: { o: Overview; onOpen: (id: string) => void }) {
  const lines: { key: keyof Movement; label: string; sign: 1 | -1 }[] = [
    { key: 'new', label: t('sub.m_new'), sign: 1 },
    { key: 'expansion', label: t('sub.m_expansion'), sign: 1 },
    { key: 'resumed', label: t('sub.m_resumed'), sign: 1 },
    { key: 'contraction', label: t('sub.m_contraction'), sign: -1 },
    { key: 'paused', label: t('sub.m_paused'), sign: -1 },
    { key: 'churn', label: t('sub.m_churn'), sign: -1 },
  ];
  return (
    <div className="shop-overview">
      <div className="form-stack">
        <section className="panel">
          <div className="prot-history-head">
            <div>
              <h2 className="rep-sub">{t('sub.mrr_trend')}</h2>
              <p className="muted small">{t('sub.mrr_trend_hint')}</p>
            </div>
          </div>
          <Bars items={o.trend.map((p, i, all) => ({ key: p.day, label: sparse(i, all.length, monthName(p.day.slice(0, 7))), value: p.mrr, tip: `${monthName(p.day.slice(0, 7))}: ${money(p.mrr)} · ${tn('sub.n_active', p.active)}` }))} />
        </section>
        <section className="panel">
          <div>
            <h2 className="rep-sub">{t('sub.next_bills')}</h2>
            <p className="muted small">{t('sub.next_bills_hint')}</p>
          </div>
          {o.due.length ? (
            <ul className="shop-todo">
              {o.due.map((s) => (
                <li key={s.id}>
                  <button className="shop-todo-row" onClick={() => onOpen(s.id)}>
                    <span className={`shop-todo-icon tone-${s.due ? 'warn' : 'info'}`}>
                      <Icon name="invoice" size={18} />
                    </span>
                    <span className="shop-todo-main">
                      <strong>
                        {subName(s.number)} · {s.customer_name}
                      </strong>
                      <span className="muted">
                        {[s.plan_name, howOften(s.every, s.unit), s.due ? t('sub.due_now') : t('sub.bills_on', { day: day(s.next_invoice) })].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                    <span className="shop-todo-end">
                      <strong className="tabular">{money(s.total)}</strong>
                      <span className="muted small">{s.auto_issue ? t('sub.issued_auto') : t('sub.as_draft')}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState icon="checkCircle" title={t('sub.none_due')} />
          )}
        </section>
      </div>
      <div className="shop-side">
        <section className="panel">
          <h2 className="rep-sub">{t('sub.this_month')}</h2>
          <dl className="sub-moves">
            {lines.map((l) => (
              <div key={l.key} className={o.month[l.key] ? '' : 'zero'}>
                <dt>{l.label}</dt>
                <dd className={`tabular ${l.sign > 0 && o.month[l.key] ? 'up' : l.sign < 0 && o.month[l.key] ? 'down' : ''}`}>
                  {o.month[l.key] ? `${l.sign > 0 ? '+' : '−'}${money(o.month[l.key])}` : '—'}
                </dd>
              </div>
            ))}
            <div className="total">
              <dt>{t('sub.m_net')}</dt>
              <dd className={`tabular ${o.month.net > 0 ? 'up' : o.month.net < 0 ? 'down' : ''}`}>
                {o.month.net > 0 ? '+' : o.month.net < 0 ? '−' : ''}
                {money(Math.abs(o.month.net))}
              </dd>
            </div>
          </dl>
          <p className="muted small">{t('sub.month_counts', { started: o.month.started, stopped: o.month.stopped })}</p>
        </section>
        {o.held ? (
          <p className="callout callout-warn">
            <Icon name="alert" size={16} />
            <span>
              {tn('sub.held_note', o.held)}{' '}
              <Link className="link" to="/invoices">
                {t('sub.open_invoices')}
              </Link>
            </span>
          </p>
        ) : null}
        <section className="panel">
          <h2 className="rep-sub">{t('sub.behind')}</h2>
          {o.owing.length ? (
            <ul className="shop-ways">
              {o.owing.map((c) => (
                <li key={c.id}>
                  <span className="vat-party">
                    <span>{c.name}</span>
                    <span className="muted small">{t('sub.behind_since', { n: c.invoices, day: day(c.oldest) })}</span>
                  </span>
                  <strong className="tabular text-danger">{money(c.overdue)}</strong>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">{t('sub.nobody_behind')}</p>
          )}
        </section>
      </div>
    </div>
  );
}

// ---- list ---------------------------------------------------------------------------------------

const FILTERS: Filter[] = ['running', 'active', 'paused', 'ending', 'draft', 'closed', 'all'];

function ListTab({ params, set, onOpen }: { params: URLSearchParams; set: (n: Record<string, string | null>) => void; onOpen: (id: string) => void }) {
  const filter = (FILTERS.includes(params.get('f') as Filter) ? params.get('f') : 'running') as Filter;
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);
  const { data } = useAction<Row[]>('recurring.subscriptions', { filter, ...(q ? { search: q } : {}) });
  const csv = () =>
    data &&
    downloadCsv(
      `subscriptions-${filter}.csv`,
      [t('sub.c_sub'), t('sub.c_customer'), t('sub.plan'), t('sub.how_often'), t('sub.c_next'), t('sub.c_period'), t('sub.k_mrr'), t('sub.c_state')],
      data.map((s) => [subName(s.number), s.customer_name, s.plan_name ?? '', howOften(s.every, s.unit), s.state === 'active' ? s.next_invoice : '', (s.total / 100).toFixed(2), (s.mrr / 100).toFixed(2), t(`sub.st_${s.state}` as MessageKey)]),
    );
  return (
    <section className="panel">
      <div className="toolbar">
        <div className="segmented subtle sub-filters" role="radiogroup" aria-label={t('sub.show')}>
          {FILTERS.map((f) => (
            <button key={f} role="radio" aria-checked={filter === f} className={filter === f ? 'active' : ''} onClick={() => set({ f: f === 'running' ? null : f })}>
              {t(`sub.f_${f}` as MessageKey)}
            </button>
          ))}
        </div>
        <span className="row-actions">
          <label className="search">
            <Icon name="search" size={16} />
            <input className="input" type="search" value={search} placeholder={t('sub.search')} aria-label={t('sub.search')} onChange={(e) => setSearch(e.target.value)} />
          </label>
          <IconButton icon="download" label={t('sub.csv')} onClick={csv} disabled={!data?.length} />
        </span>
      </div>
      {!data ? (
        <Skeleton h={240} />
      ) : (
        <Table
          view="subscriptions.list"
          head={[t('sub.c_sub'), t('sub.c_customer'), t('sub.how_often'), t('sub.c_next'), t('sub.k_mrr'), t('sub.c_state')]}
          phoneHide={[0, 2, 3]}
          left={[1, 2, 3]}
          empty={filter === 'running' ? t('sub.none_running') : t('sub.none_found')}
          rows={data.map((s) => ({
            key: s.id,
            onClick: () => onOpen(s.id),
            cells: [
              <strong key="n" className="tabular">
                {subName(s.number)}
              </strong>,
              <span className="vat-party" key="c">
                <span>{s.customer_name}</span>
                <span className="muted">
                  <span className="shop-phone-only">{subName(s.number)} · </span>
                  {s.plan_name ?? t('sub.own_terms')}
                </span>
              </span>,
              <span key="o" className="vat-party">
                <span>{howOften(s.every, s.unit)}</span>
                <span className="muted tabular">{money(s.total)}</span>
              </span>,
              <span key="x" className="tabular">
                {s.state === 'active' ? (s.end_date && s.next_invoice > s.end_date ? t('sub.ends_on', { day: day(s.end_date) }) : day(s.next_invoice)) : s.state === 'draft' ? t('sub.starts_on', { day: day(s.start_date) }) : '—'}
              </span>,
              <span key="m" className="tabular">
                {s.state === 'active' ? money(s.mrr) : <span className="muted">{money(s.mrr)}</span>}
              </span>,
              <SubBadge key="s" s={s} />,
            ],
          }))}
        />
      )}
    </section>
  );
}

// ---- one subscription ------------------------------------------------------------------------------

const EVENT_ICON: Record<HistoryRow['kind'], IconName> = {
  started: 'checkCircle',
  changed: 'edit',
  paused: 'pause',
  resumed: 'undo',
  closed: 'x',
  invoiced: 'invoice',
  held: 'alert',
};

function SubDialog({ id, onClose, onChange, onEdit }: { id: string; onClose: () => void; onChange: () => Promise<void>; onEdit: (id: string) => void }) {
  const { can } = useSession();
  const { busy, run } = useSave();
  const { data: s, reload } = useAction<Detail>('recurring.subscription', { id });
  const [ask, setAsk] = useState<null | 'stop' | 'pause' | 'early'>(null);
  const [note, setNote] = useState('');
  const manage = can('recurring.manage');
  const done = async () => {
    setAsk(null);
    await reload();
    await onChange();
  };
  const act = (name: string, input: Record<string, unknown>, msg: string) =>
    void run(async () => {
      await action(name, { id, ...input });
      await done();
    }, msg);
  if (!s) {
    return (
      <Dialog title={t('sub.subscription')} onClose={onClose} size="lg">
        <Skeleton h={320} />
      </Dialog>
    );
  }
  const held = s.history.filter((h) => h.kind === 'held' && h.invoice_state === 'draft');
  const rates = byRate(s.lines.map((l) => ({ ...lineFigures(l.unit_price, l.qty, l.discount, l.tax_rate), tax_rate: l.tax_rate })));
  return (
    <>
      <Dialog
        title={`${subName(s.number)} · ${s.customer_name}`}
        onClose={onClose}
        size="lg"
        footer={
          manage && s.state !== 'closed' ? (
            <>
              <Button variant="ghost" icon="x" onClick={() => setAsk('stop')}>
                {s.state === 'draft' ? t('sub.discard') : t('sub.stop')}
              </Button>
              {s.state === 'active' ? (
                <Button variant="ghost" icon="pause" onClick={() => setAsk('pause')}>
                  {t('sub.pause')}
                </Button>
              ) : null}
              <Button icon="edit" onClick={() => onEdit(s.id)}>
                {t('sub.change')}
              </Button>
              {s.state === 'draft' ? (
                <Button variant="primary" icon="check" busy={busy} onClick={() => act('recurring.start', {}, t('sub.started_msg', { n: subName(s.number) }))}>
                  {t('sub.start')}
                </Button>
              ) : s.state === 'paused' ? (
                <Button variant="primary" icon="undo" busy={busy} onClick={() => act('recurring.resume', {}, t('sub.resumed_msg'))}>
                  {t('sub.resume')}
                </Button>
              ) : s.ending ? (
                <Button variant="primary" icon="check" busy={busy} onClick={() => act('recurring.keep', {}, t('sub.kept_msg'))}>
                  {t('sub.keep')}
                </Button>
              ) : (
                <Button variant="primary" icon="invoice" onClick={() => setAsk('early')}>
                  {t('sub.bill_next')}
                </Button>
              )}
            </>
          ) : undefined
        }
      >
        <div className="form-stack">
          <div className="item-summary">
            <div>
              <span className="muted">{t('sub.each_period', { period: perPeriod(s.every, s.unit) })}</span>
              <strong className="tabular">{money(s.total)}</strong>
            </div>
            <div>
              <span className="muted">{t('sub.k_mrr')}</span>
              <strong className="tabular">{money(s.mrr)}</strong>
            </div>
            <div>
              <span className="muted">{s.state === 'active' ? t('sub.c_next') : s.state === 'draft' ? t('sub.starts') : t('sub.billed_so_far')}</span>
              <strong>{s.state === 'active' ? day(s.next_invoice) : s.state === 'draft' ? day(s.start_date) : money(s.billed)}</strong>
            </div>
            <SubBadge s={s} />
          </div>

          {held.length ? (
            <p className="callout callout-warn">
              <Icon name="alert" size={16} />
              <span>
                {tn('sub.held_here', held.length)} {held[0]!.note ? `(${held[0]!.note})` : ''}{' '}
                <Link className="link" to={`/invoices?open=${held[0]!.invoice}`}>
                  {t('sub.open_invoice')}
                </Link>
              </span>
            </p>
          ) : null}
          {s.state === 'active' && s.ending && s.end_date ? (
            <p className="callout callout-warn">
              <Icon name="clock" size={16} />
              <span>{s.close_reason ? t('sub.stops_on', { day: day(s.end_date), reason: t(`sub.r_${s.close_reason}` as MessageKey) }) : t('sub.ends_fixed', { day: day(s.end_date) })}</span>
            </p>
          ) : s.state === 'paused' ? (
            <p className="callout callout-warn">
              <Icon name="pause" size={16} />
              <span>{t('sub.paused_note', { at: s.paused_at ? dateTime(s.paused_at) : '' })}</span>
            </p>
          ) : s.state === 'draft' ? (
            <p className="callout callout-info">
              <Icon name="note" size={16} />
              <span>{t('sub.draft_note', { day: day(s.start_date) })}</span>
            </p>
          ) : s.state === 'closed' ? (
            <p className="callout">
              <Icon name="x" size={16} />
              <span>{[t('sub.closed_note', { day: s.closed_at ? dateTime(s.closed_at) : '' }), s.close_reason ? t(`sub.r_${s.close_reason}` as MessageKey) : null, s.close_note].filter(Boolean).join(' · ')}</span>
            </p>
          ) : null}

          <div className="shop-detail">
            <section>
              <h3 className="rep-sub">{t('sub.what_in_it')}</h3>
              <table className="shop-items">
                <tbody>
                  {s.lines.map((l, i) => (
                    <tr key={i}>
                      <td className="tabular">{qtyText(l.qty)} ×</td>
                      <td>
                        {l.name}
                        {l.discount ? <span className="muted small"> · −{l.discount}%</span> : null}
                      </td>
                      <td className="tabular muted">{money(l.unit_price)}</td>
                      <td className="tabular">{money(lineFigures(l.unit_price, l.qty, l.discount, l.tax_rate).net)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Totals net={s.net} tax={s.tax} rates={rates} />
              {s.upcoming.length ? (
                <>
                  <h3 className="rep-sub">{t('sub.next_periods')}</h3>
                  <ul className="sub-periods">
                    {s.upcoming.map((p) => (
                      <li key={p.start}>
                        <Icon name="calendar" size={14} />
                        <span>
                          {day(p.start)} – {day(p.end)}
                        </span>
                        <span className="muted small">{t('sub.billed_on', { day: day(p.start) })}</span>
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
            </section>
            <section>
              <h3 className="rep-sub">{t('sub.details')}</h3>
              <dl className="appr-facts">
                <dt>{t('sub.c_customer')}</dt>
                <dd>
                  <Link className="link" to={`/invoices?tab=customers`}>
                    {s.customer_name}
                  </Link>
                </dd>
                <dt>{t('sub.plan')}</dt>
                <dd>{s.plan_name ?? t('sub.own_terms')}</dd>
                <dt>{t('sub.how_often')}</dt>
                <dd>{howOften(s.every, s.unit)}</dd>
                <dt>{t('sub.started')}</dt>
                <dd>{day(s.start_date)}</dd>
                {s.current ? (
                  <>
                    <dt>{t('sub.paid_period')}</dt>
                    <dd>
                      {day(s.current.start)} – {day(s.current.end)}
                    </dd>
                  </>
                ) : null}
                <dt>{t('sub.invoices')}</dt>
                <dd>{s.auto_issue ? t('sub.issued_auto_long') : t('sub.as_draft_long')}</dd>
                {s.reference ? (
                  <>
                    <dt>{t('sub.reference')}</dt>
                    <dd>{s.reference}</dd>
                  </>
                ) : null}
                <dt>{t('sub.billed_so_far')}</dt>
                <dd className="tabular">
                  {money(s.billed)}
                  {s.open ? <span className="text-danger"> · {t('sub.still_open', { amount: money(s.open) })}</span> : null}
                </dd>
                {s.note ? (
                  <>
                    <dt>{t('sub.note')}</dt>
                    <dd>“{s.note}”</dd>
                  </>
                ) : null}
              </dl>
            </section>
          </div>

          <section>
            <h3 className="rep-sub">{t('sub.history')}</h3>
            {s.history.length ? (
              <ol className="sub-history">
                {s.history.map((h) => (
                  <li key={h.id} className={`k-${h.kind}`}>
                    <span className="sub-h-icon" aria-hidden="true">
                      <Icon name={EVENT_ICON[h.kind]} size={14} />
                    </span>
                    <span className="sub-h-main">
                      <strong>
                        {h.invoice ? (
                          <Link className="link" to={`/invoices?open=${h.invoice}`}>
                            {h.invoice_name ? t('sub.invoice_no', { n: h.invoice_name }) : t('sub.draft_invoice')}
                          </Link>
                        ) : (
                          t(`sub.ev_${h.kind}` as MessageKey)
                        )}
                      </strong>
                      <span className="muted small">
                        {[
                          h.period_start && h.period_end ? `${day(h.period_start)} – ${day(h.period_end)}` : null,
                          h.kind === 'changed' && h.mrr_before !== h.mrr_after ? t('sub.mrr_moved', { from: money(h.mrr_before), to: money(h.mrr_after) }) : null,
                          h.kind === 'held' ? h.note : h.kind === 'invoiced' ? null : h.note,
                          h.by_name ?? (h.kind === 'invoiced' || h.kind === 'held' ? t('sub.by_system') : null),
                          dateTime(h.at),
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </span>
                    {h.invoice ? (
                      <span className="sub-h-end">
                        <strong className="tabular">{money(h.invoice_total ?? 0)}</strong>
                        <InvoiceState h={h} />
                      </span>
                    ) : null}
                  </li>
                ))}
              </ol>
            ) : (
              <p className="muted">{t('sub.no_history')}</p>
            )}
          </section>
        </div>
      </Dialog>
      {ask === 'stop' ? <StopDialog s={s} onClose={() => setAsk(null)} onDone={done} /> : null}
      {ask === 'pause' ? (
        <Dialog
          title={t('sub.pause_title', { n: subName(s.number) })}
          onClose={() => setAsk(null)}
          size="sm"
          footer={
            <Button variant="primary" icon="pause" busy={busy} onClick={() => act('recurring.pause', { note: note.trim() || null }, t('sub.paused_msg'))}>
              {t('sub.pause')}
            </Button>
          }
        >
          <p className="muted">{t('sub.pause_note')}</p>
          <Field label={t('sub.why')} hint={t('sub.optional')}>
            <input className="input" value={note} maxLength={500} placeholder={t('sub.pause_example')} onChange={(e) => setNote(e.target.value)} />
          </Field>
        </Dialog>
      ) : null}
      {ask === 'early' ? (
        <Dialog
          title={t('sub.bill_next_title', { n: subName(s.number) })}
          onClose={() => setAsk(null)}
          size="sm"
          footer={
            <Button variant="primary" icon="invoice" busy={busy} onClick={() => act('recurring.invoice_now', {}, t('sub.billed_msg'))}>
              {t('sub.bill_amount', { amount: money(s.total) })}
            </Button>
          }
        >
          <p>{s.upcoming[0] ? t('sub.bill_next_note', { from: day(s.upcoming[0].start), to: day(s.upcoming[0].end) }) : null}</p>
          <p className="muted small">{t('sub.bill_next_hint')}</p>
        </Dialog>
      ) : null}
    </>
  );
}

function InvoiceState({ h }: { h: HistoryRow }) {
  if (h.invoice_state === 'paid') return <StatusBadge tone="ok" icon="check" label={t('sub.inv_paid')} />;
  if (h.invoice_state === 'draft') return <StatusBadge tone="warn" icon="edit" label={t('sub.inv_draft')} />;
  if (h.invoice_state === 'cancelled') return <StatusBadge tone="neutral" icon="x" label={t('sub.inv_cancelled')} />;
  const late = !!h.invoice_due && h.invoice_due < new Date().toISOString().slice(0, 10);
  return late ? <StatusBadge tone="danger" icon="alert" label={t('sub.inv_late')} /> : <StatusBadge tone="info" icon="clock" label={t('sub.inv_open')} />;
}

function StopDialog({ s, onClose, onDone }: { s: Detail; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [reason, setReason] = useState<Reason>('price');
  const [note, setNote] = useState('');
  const [when, setWhen] = useState<'end' | 'now'>(s.state === 'active' ? 'end' : 'now');
  const [tried, setTried] = useState(false);
  const draft = s.state === 'draft';
  const needNote = reason === 'other' && !note.trim();
  const go = () => {
    setTried(true);
    if (needNote) return;
    void run(async () => {
      await action('recurring.stop', { id: s.id, reason, note: note.trim() || null, at_period_end: when === 'end' && s.state === 'active' });
      await onDone();
    }, draft ? t('sub.discarded_msg') : when === 'end' && s.state === 'active' ? t('sub.stop_planned_msg') : t('sub.stopped_msg'));
  };
  return (
    <Dialog
      title={draft ? t('sub.discard_title', { n: subName(s.number) }) : t('sub.stop_title', { n: subName(s.number) })}
      onClose={onClose}
      size="sm"
      footer={
        <Button variant="danger" icon="x" busy={busy} onClick={go}>
          {draft ? t('sub.discard') : when === 'end' && s.state === 'active' ? t('sub.stop_at_end') : t('sub.stop_now')}
        </Button>
      }
    >
      <div className="form-stack">
        {!draft && s.state === 'active' ? (
          <div className="option-list" role="radiogroup" aria-label={t('sub.when')}>
            {(['end', 'now'] as const).map((w) => (
              <label key={w} className={`option-card${when === w ? ' selected' : ''}`}>
                <input type="radio" name="when" checked={when === w} onChange={() => setWhen(w)} />
                <span>
                  <strong>{w === 'end' ? t('sub.when_end', { day: day(s.current?.end ?? s.next_invoice) }) : t('sub.when_now')}</strong>
                  <span className="muted small">{w === 'end' ? t('sub.when_end_hint') : t('sub.when_now_hint')}</span>
                </span>
              </label>
            ))}
          </div>
        ) : null}
        <Field label={t('sub.reason')}>
          <select className="input" value={reason} onChange={(e) => setReason(e.target.value as Reason)}>
            {REASONS.map((r) => (
              <option key={r} value={r}>
                {t(`sub.r_${r}` as MessageKey)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('sub.note')} error={tried && needNote ? t('sub.e_reason') : null} hint={reason === 'other' ? undefined : t('sub.optional')}>
          <input className="input" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

// ---- new or changed subscription ---------------------------------------------------------------

function LinesEditor({ lines, setLines, products }: { lines: EditLine[]; setLines: (l: EditLine[]) => void; products: Sellable[] }) {
  const byId = new Map(products.map((p) => [p.id, p]));
  const set = (i: number, patch: Partial<EditLine>) =>
    setLines(
      lines.map((l, j) => {
        if (j !== i) return l;
        const next = { ...l, ...patch };
        const p = patch.product ? byId.get(patch.product) : undefined;
        if (p) Object.assign(next, { name: p.name, price: priceText(p.price), vat: knownRateOr(p.tax_rate) });
        return next;
      }),
    );
  return (
    <>
      <div className="inv-lines" role="table" aria-label={t('sub.what_in_it')}>
        <div className="inv-line head" role="row">
          <span role="columnheader">{t('inv.what')}</span>
          <span role="columnheader">{t('inv.qty')}</span>
          <span role="columnheader">{t('inv.unit_price')}</span>
          <span role="columnheader">{t('inv.discount')}</span>
          <span role="columnheader">{t('inv.vat')}</span>
          <span role="columnheader" className="num">
            {t('inv.net')}
          </span>
          <span />
        </div>
        {lines.map((l, i) => {
          const qty = parseNumber(l.qty);
          const price = parseMoney(l.price);
          const discount = l.discount.trim() ? parseNumber(l.discount) : 0;
          const ok = !!l.name.trim() && qty !== null && qty > 0 && price !== null && discount !== null && discount >= 0 && discount <= 100;
          const touched = !!(l.name.trim() || l.price.trim() || l.product);
          return (
            <div key={i} className="inv-line" role="row">
              <span className="inv-what">
                <select className="input" aria-label={t('inv.product')} value={l.product} onChange={(e) => set(i, { product: e.target.value })}>
                  <option value="">{t('inv.typed_line')}</option>
                  {products.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name}
                    </option>
                  ))}
                </select>
                <input className={`input${touched && !l.name.trim() ? ' invalid' : ''}`} aria-label={t('inv.description')} placeholder={t('inv.description')} value={l.name} maxLength={200} onChange={(e) => set(i, { name: e.target.value })} />
              </span>
              <span className="inv-qty">
                <input className={`input${touched && (qty === null || qty <= 0) ? ' invalid' : ''}`} inputMode="decimal" aria-label={t('inv.qty')} value={l.qty} onChange={(e) => set(i, { qty: e.target.value })} />
                <select className="input" aria-label={t('inv.unit')} value={l.unit} onChange={(e) => set(i, { unit: e.target.value as Unit })}>
                  {UNITS.map((u) => (
                    <option key={u} value={u}>
                      {t(`inv.u_${u}` as MessageKey)}
                    </option>
                  ))}
                </select>
              </span>
              <input className={`input${touched && price === null ? ' invalid' : ''}`} inputMode="decimal" aria-label={t('inv.unit_price')} placeholder="0" value={l.price} onChange={(e) => set(i, { price: e.target.value })} />
              <span className="input-unit">
                <input className={`input${discount === null || (discount ?? 0) > 100 ? ' invalid' : ''}`} inputMode="decimal" aria-label={t('inv.discount')} placeholder="0" value={l.discount} onChange={(e) => set(i, { discount: e.target.value })} />
                <span>%</span>
              </span>
              <TaxRateSelect label={t('inv.vat')} value={l.vat} onChange={(r) => set(i, { vat: r })} />
              <span className="num">{ok ? money(lineFigures(price!, qty!, discount!, l.vat).net) : '—'}</span>
              <IconButton icon="trash" label={t('inv.remove_line')} disabled={lines.length === 1} onClick={() => setLines(lines.filter((_, j) => j !== i))} />
            </div>
          );
        })}
      </div>
      <div className="row-actions">
        <Button icon="plus" onClick={() => setLines([...lines, blankLine()])}>
          {t('inv.add_line')}
        </Button>
      </div>
    </>
  );
}

/** The lines as the server wants them, the figures, and whether they are all right. */
function readLines(lines: EditLine[]) {
  const filled = lines.filter((l) => l.name.trim() || l.price.trim() || l.product);
  const out: SubLine[] = [];
  let bad = false;
  for (const l of filled) {
    const qty = parseNumber(l.qty);
    const price = parseMoney(l.price);
    const discount = l.discount.trim() ? parseNumber(l.discount) : 0;
    if (!l.name.trim() || qty === null || qty <= 0 || price === null || discount === null || discount < 0 || discount > 100) {
      bad = true;
      continue;
    }
    out.push({ product: l.product || null, name: l.name.trim(), unit: l.unit, qty, unit_price: price, discount, tax_rate: l.vat });
  }
  const figures = out.map((l) => ({ ...lineFigures(l.unit_price, l.qty, l.discount, l.tax_rate), tax_rate: l.tax_rate }));
  return { lines: out, bad, net: figures.reduce((s, f) => s + f.net, 0), tax: figures.reduce((s, f) => s + f.tax, 0), rates: byRate(figures) };
}

function PeriodFields({ every, unit, setEvery, setUnit, disabled }: { every: number; unit: PeriodUnit; setEvery: (n: number) => void; setUnit: (u: PeriodUnit) => void; disabled?: boolean }) {
  const presets: { every: number; unit: PeriodUnit; key: string }[] = [
    { every: 1, unit: 'week', key: 'w' },
    { every: 1, unit: 'month', key: 'm' },
    { every: 3, unit: 'month', key: 'q' },
    { every: 6, unit: 'month', key: 'h' },
    { every: 1, unit: 'year', key: 'y' },
  ];
  const custom = !presets.some((p) => p.every === every && p.unit === unit);
  return (
    <Field label={t('sub.how_often')} hint={disabled ? t('sub.how_often_locked') : undefined}>
      <div className="sub-period">
        <div className="segmented subtle" role="radiogroup" aria-label={t('sub.how_often')}>
          {presets.map((p) => (
            <button key={p.key} type="button" role="radio" disabled={disabled} aria-checked={p.every === every && p.unit === unit} className={p.every === every && p.unit === unit ? 'active' : ''} onClick={() => (setEvery(p.every), setUnit(p.unit))}>
              {t(`sub.p_${p.key}` as MessageKey)}
            </button>
          ))}
        </div>
        {custom || !disabled ? (
          <span className="sub-period-custom">
            <span className="muted small">{t('sub.or_every')}</span>
            <input className="input" inputMode="numeric" aria-label={t('sub.every_count')} disabled={disabled} value={every} onChange={(e) => setEvery(Math.max(1, Math.min(36, Number(e.target.value.replace(/\D/g, '')) || 1)))} />
            <select className="input" aria-label={t('sub.every_unit')} disabled={disabled} value={unit} onChange={(e) => setUnit(e.target.value as PeriodUnit)}>
              {(['week', 'month', 'year'] as const).map((u) => (
                <option key={u} value={u}>
                  {t(`sub.u_${u}` as MessageKey)}
                </option>
              ))}
            </select>
          </span>
        ) : null}
      </div>
    </Field>
  );
}

function SubForm({ id, plan: planId, onClose, onSaved }: { id?: string; plan?: string; onClose: () => void; onSaved: (id: string) => Promise<void> }) {
  const { fail } = useUi();
  const { busy, run } = useSave();
  const { data: setup } = useAction<{ today: string; plans: Plan[] }>('recurring.setup');
  const { data: customers } = useAction<Customer[]>('invoicing.customers', { filter: 'all' });
  const [old, setOld] = useState<Detail | null>(null);
  const [customer, setCustomer] = useState('');
  const { data: products } = useAction<Sellable[]>('invoicing.products', { customer: customer || null });
  const [plan, setPlan] = useState<string>(planId ?? '');
  const [every, setEvery] = useState(1);
  const [unit, setUnit] = useState<PeriodUnit>('month');
  const [lines, setLines] = useState<EditLine[]>([blankLine()]);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [auto, setAuto] = useState(true);
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [billChange, setBillChange] = useState(true);
  const [tried, setTried] = useState(false);
  const [ready, setReady] = useState(false);

  const usePlan = (p: Plan | undefined) => {
    if (!p) return;
    setEvery(p.every);
    setUnit(p.unit);
    setAuto(p.auto_issue);
    if (p.lines.length) setLines(p.lines.map(toEdit));
  };
  useEffect(() => {
    if (!setup || ready) return;
    if (id) {
      void action<Detail>('recurring.subscription', { id }).then((d) => {
        setOld(d);
        setCustomer(d.customer);
        setPlan(d.plan ?? '');
        setEvery(d.every);
        setUnit(d.unit);
        setLines(d.lines.map(toEdit));
        setStart(d.start_date);
        setEnd(d.end_date ?? '');
        setAuto(d.auto_issue);
        setReference(d.reference ?? '');
        setNote(d.note ?? '');
        setReady(true);
      }, fail);
      return;
    }
    setStart(setup.today);
    usePlan(setup.plans.find((p) => p.id === planId));
    setReady(true);
  }, [setup]);

  const running = !!old && old.state !== 'draft';
  const r = readLines(lines);
  const mrr = monthlyOf(r.net, every, unit);
  const linesChanged = running && JSON.stringify(old!.lines.map((l) => [l.product, l.name, l.unit, l.qty, l.unit_price, l.discount, l.tax_rate])) !== JSON.stringify(r.lines.map((l) => [l.product, l.name, l.unit, l.qty, l.unit_price, l.discount, l.tax_rate]));
  const more = running && r.net > old!.net && old!.state === 'active';
  const errors = {
    customer: !customer ? t('sub.e_customer') : null,
    lines: r.bad ? t('sub.e_lines_bad') : !r.lines.length ? t('sub.e_lines') : null,
    start: !start ? t('sub.e_start') : !running && setup && start < addDaysIso(setup.today, -365) ? t('sub.e_start_old') : null,
    end: end && end < start ? t('sub.e_end') : running && end && old && end < addDaysIso(old.next_invoice, -1) ? t('sub.e_end_billed', { day: day(addDaysIso(old.next_invoice, -1)) }) : null,
  };
  const save = (thenStart: boolean) => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    void run(
      async () => {
        const saved = await action<Row>('recurring.save', {
          id: id ?? null,
          customer,
          plan: plan || null,
          every,
          unit,
          lines: r.lines,
          start_date: start,
          end_date: end || null,
          auto_issue: auto,
          reference: reference.trim() || null,
          note: note.trim() || null,
          bill_change: linesChanged && more && billChange,
        });
        if (thenStart) await action('recurring.start', { id: saved.id });
        await onSaved(saved.id);
      },
      thenStart ? t('sub.started_ok') : t('sub.saved_msg'),
    );
  };
  const err = (e: string | null) => (tried ? e : null);
  const cust = customers?.find((c) => c.id === customer);
  const today = setup?.today ?? '';

  return (
    <Dialog
      title={old ? t('sub.change_title', { n: subName(old.number) }) : t('sub.new')}
      onClose={onClose}
      size="xl"
      footer={
        <>
          <span className="inv-foot-total">
            <span className="muted">{t('sub.each_period', { period: perPeriod(every, unit) })}</span>
            <strong>{money(r.net + r.tax)}</strong>
            <span className="muted small">{t('sub.mrr_short', { amount: money(mrr) })}</span>
          </span>
          <span className="foot-actions">
            {running ? (
              <Button variant="primary" icon="check" busy={busy} onClick={() => save(false)}>
                {t('common.save')}
              </Button>
            ) : (
              <>
                <Button icon="edit" busy={busy} onClick={() => save(false)}>
                  {t('sub.save_draft')}
                </Button>
                <Button variant="primary" icon="check" busy={busy} onClick={() => save(true)}>
                  {start && start <= today ? t('sub.start_bill') : t('sub.start')}
                </Button>
              </>
            )}
          </span>
        </>
      }
    >
      {!setup || !customers || !products || !ready ? (
        <Skeleton h={360} />
      ) : (
        <div className="form-stack sub-form">
          <div className="form-grid">
            <Field label={t('sub.c_customer')} required error={err(errors.customer)} hint={cust ? customerHint(cust) : undefined}>
              <select className="input" value={customer} disabled={running} onChange={(e) => setCustomer(e.target.value)}>
                <option value="">{t('inv.pick_customer')}</option>
                {customers
                  .filter((c) => c.active || c.id === customer)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                      {c.tax_id ? ` · ${c.tax_id}` : ''}
                    </option>
                  ))}
              </select>
            </Field>
            <Field label={t('sub.plan')} hint={running ? undefined : t('sub.plan_hint')}>
              <select
                className="input"
                value={plan}
                onChange={(e) => {
                  setPlan(e.target.value);
                  if (!running) usePlan(setup.plans.find((p) => p.id === e.target.value));
                }}
              >
                <option value="">{t('sub.own_terms')}</option>
                {setup.plans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} · {howOften(p.every, p.unit)}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <PeriodFields every={every} unit={unit} setEvery={setEvery} setUnit={setUnit} disabled={running} />
          <div className="form-grid">
            <Field label={t('sub.start_date')} required error={err(errors.start)} hint={running ? t('sub.start_locked') : start && start < today ? t('sub.start_past') : t('sub.start_hint')}>
              <input className="input" type="date" value={start} disabled={running} onChange={(e) => setStart(e.target.value)} />
            </Field>
            <Field label={t('sub.end_date')} error={err(errors.end)} hint={t('sub.end_hint')}>
              <input className="input" type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)} />
            </Field>
            <Field label={t('sub.reference')} hint={t('sub.reference_hint')}>
              <input className="input" value={reference} maxLength={80} onChange={(e) => setReference(e.target.value)} />
            </Field>
          </div>
          <fieldset className="option-group">
            <legend>{t('sub.what_in_it')}</legend>
            <LinesEditor lines={lines} setLines={setLines} products={products} />
            {err(errors.lines) ? (
              <p className="field-msg" role="alert">
                <Icon name="alert" size={14} /> {errors.lines}
              </p>
            ) : null}
            <Totals net={r.net} tax={r.tax} rates={r.rates} />
          </fieldset>
          {linesChanged ? (
            <div className="setting-row callout callout-info">
              <div>
                <strong>{more ? t('sub.change_more') : t('sub.change_less')}</strong>
                <p className="muted">{more ? t('sub.change_more_hint') : t('sub.change_less_hint')}</p>
              </div>
              {more ? <Switch checked={billChange} onChange={setBillChange} label={t('sub.change_more')} /> : null}
            </div>
          ) : null}
          <div className="setting-row">
            <div>
              <strong>{t('sub.auto_issue')}</strong>
              <p className="muted">{t('sub.auto_issue_hint')}</p>
            </div>
            <Switch checked={auto} onChange={setAuto} label={t('sub.auto_issue')} />
          </div>
          <Field label={t('sub.note')} counter={`${note.length}/1000`}>
            <textarea className="input textarea" rows={2} value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} />
          </Field>
        </div>
      )}
    </Dialog>
  );
}

function addDaysIso(d: string, n: number) {
  const x = new Date(`${d}T00:00:00Z`);
  x.setUTCDate(x.getUTCDate() + n);
  return x.toISOString().slice(0, 10);
}

// ---- plans ------------------------------------------------------------------------------------------

function PlansTab({ onUse }: { onUse?: (plan: string) => void }) {
  const { can } = useSession();
  const { data, reload } = useAction<Plan[]>('recurring.plans');
  const [edit, setEdit] = useState<Plan | 'new' | null>(null);
  const manage = can('recurring.plans');
  return (
    <section className="panel">
      <div className="prot-history-head">
        <div>
          <h2 className="rep-sub">{t('sub.plans_title')}</h2>
          <p className="muted small">{t('sub.plans_hint')}</p>
        </div>
        {manage ? (
          <Button size="sm" icon="plus" onClick={() => setEdit('new')}>
            {t('sub.new_plan')}
          </Button>
        ) : null}
      </div>
      {!data ? (
        <Skeleton h={200} />
      ) : data.length ? (
        <ul className="sub-plans">
          {data.map((p) => (
            <li key={p.id} className={p.active ? '' : 'inactive'}>
              <div className="sub-plan-head">
                <strong>{p.name}</strong>
                {p.active ? null : <StatusBadge tone="neutral" icon="archive" label={t('sub.plan_off')} />}
              </div>
              <span className="sub-plan-price">
                <strong className="tabular">{money(p.total ?? 0)}</strong>
                <span className="muted small">{perPeriod(p.every, p.unit)}</span>
              </span>
              {p.description ? <p className="muted small">{p.description}</p> : null}
              <span className="muted small">{[tn('sub.n_active', p.subscribers ?? 0), t('sub.mrr_short', { amount: money(p.mrr ?? 0) })].join(' · ')}</span>
              <div className="row-actions">
                {onUse && p.active ? (
                  <Button size="sm" icon="plus" onClick={() => onUse(p.id)}>
                    {t('sub.use_plan')}
                  </Button>
                ) : null}
                {manage ? (
                  <Button size="sm" variant="ghost" icon="edit" onClick={() => setEdit(p)}>
                    {t('sub.edit_plan')}
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState icon="layers" title={t('sub.no_plans')} body={t('sub.no_plans_hint')} action={manage ? <Button icon="plus" onClick={() => setEdit('new')}>{t('sub.new_plan')}</Button> : undefined} />
      )}
      {edit ? (
        <PlanDialog
          plan={edit === 'new' ? null : edit}
          onClose={() => setEdit(null)}
          onSaved={async () => {
            setEdit(null);
            await reload();
          }}
        />
      ) : null}
    </section>
  );
}

function PlanDialog({ plan, onClose, onSaved }: { plan: Plan | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const { data: products } = useAction<Sellable[]>('invoicing.products', { customer: null });
  const [name, setName] = useState(plan?.name ?? '');
  const [every, setEvery] = useState(plan?.every ?? 1);
  const [unit, setUnit] = useState<PeriodUnit>(plan?.unit ?? 'month');
  const [lines, setLines] = useState<EditLine[]>(plan?.lines.length ? plan.lines.map(toEdit) : [blankLine()]);
  const [auto, setAuto] = useState(plan?.auto_issue ?? true);
  const [description, setDescription] = useState(plan?.description ?? '');
  const [active, setActive] = useState(plan?.active ?? true);
  const [tried, setTried] = useState(false);
  const r = readLines(lines);
  const errors = { name: !name.trim() ? t('sub.e_name') : null, lines: r.bad ? t('sub.e_lines_bad') : null };
  const save = () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    void run(async () => {
      await action('recurring.save_plan', { id: plan?.id ?? null, name: name.trim(), every, unit, lines: r.lines, auto_issue: auto, description: description.trim() || null, sequence: plan?.sequence ?? 0, active });
      await onSaved();
    }, t('sub.plan_saved'));
  };
  return (
    <Dialog
      title={plan ? plan.name : t('sub.new_plan')}
      onClose={onClose}
      size="xl"
      footer={
        <>
          <span className="inv-foot-total">
            <span className="muted">{t('sub.each_period', { period: perPeriod(every, unit) })}</span>
            <strong>{money(r.net + r.tax)}</strong>
          </span>
          <span className="foot-actions">
            <Button variant="primary" icon="check" busy={busy} onClick={save}>
              {t('common.save')}
            </Button>
          </span>
        </>
      }
    >
      {!products ? (
        <Skeleton h={300} />
      ) : (
        <div className="form-stack sub-form">
          <Field label={t('sub.plan_name')} required error={tried ? errors.name : null}>
            <input className="input" value={name} maxLength={80} placeholder={t('sub.plan_example')} onChange={(e) => setName(e.target.value)} />
          </Field>
          <PeriodFields every={every} unit={unit} setEvery={setEvery} setUnit={setUnit} />
          <fieldset className="option-group">
            <legend>{t('sub.plan_lines')}</legend>
            <LinesEditor lines={lines} setLines={setLines} products={products} />
            {tried && errors.lines ? (
              <p className="field-msg" role="alert">
                <Icon name="alert" size={14} /> {errors.lines}
              </p>
            ) : (
              <p className="muted small">{t('sub.plan_lines_hint')}</p>
            )}
          </fieldset>
          <Field label={t('sub.description')} counter={`${description.length}/1000`}>
            <textarea className="input textarea" rows={2} value={description} maxLength={1000} onChange={(e) => setDescription(e.target.value)} />
          </Field>
          <div className="setting-row">
            <div>
              <strong>{t('sub.auto_issue')}</strong>
              <p className="muted">{t('sub.auto_issue_plan_hint')}</p>
            </div>
            <Switch checked={auto} onChange={setAuto} label={t('sub.auto_issue')} />
          </div>
          <div className="setting-row">
            <div>
              <strong>{t('sub.plan_on')}</strong>
              <p className="muted">{t('sub.plan_on_hint')}</p>
            </div>
            <Switch checked={active} onChange={setActive} label={t('sub.plan_on')} />
          </div>
        </div>
      )}
    </Dialog>
  );
}

// ---- results ----------------------------------------------------------------------------------------

function ResultsTab() {
  const [months, setMonths] = useState(12);
  const { data } = useAction<Report>('recurring.report', { months });
  const csv = () =>
    data &&
    downloadCsv(
      `subscriptions-mrr-${months}.csv`,
      [t('sub.c_month'), t('sub.m_new'), t('sub.m_expansion'), t('sub.m_contraction'), t('sub.m_churn'), t('sub.m_net'), t('sub.k_mrr'), t('sub.c_active'), t('sub.c_churn_rate')],
      data.months.map((m) => [m.month, m.new / 100, m.expansion / 100, -(m.contraction + m.paused) / 100, -m.churn / 100, m.net / 100, m.mrr / 100, m.active, m.churn_rate]),
    );
  const signed = (v: number, sign: 1 | -1) => (v ? <span className={sign > 0 ? 'text-ok' : 'text-danger'}>{`${sign > 0 ? '+' : '−'}${money(v)}`}</span> : <span className="muted">—</span>);
  return (
    <div className="form-stack">
      <section className="panel">
        <div className="toolbar">
          <div className="segmented subtle" role="radiogroup" aria-label={t('sub.r_period')}>
            {[6, 12, 24].map((m) => (
              <button key={m} role="radio" aria-checked={months === m} className={months === m ? 'active' : ''} onClick={() => setMonths(m)}>
                {tn('sub.n_months', m)}
              </button>
            ))}
          </div>
          <Button size="sm" icon="download" disabled={!data?.months.length} onClick={csv}>
            {t('sub.csv')}
          </Button>
        </div>
        {!data ? (
          <Skeleton h={260} />
        ) : (
          <>
            <Bars items={data.months.map((m, i, all) => ({ key: m.month, label: sparse(i, all.length, monthName(m.month)), value: m.mrr, tip: `${monthName(m.month)}: ${money(m.mrr)}` }))} />
            <Table
              head={[t('sub.c_month'), t('sub.m_new'), t('sub.m_expansion'), t('sub.m_less'), t('sub.m_churn'), t('sub.m_net'), t('sub.k_mrr'), t('sub.c_churn_rate')]}
              phoneHide={[1, 2, 3, 4, 7]}
              rows={[...data.months].reverse().map((m) => ({
                key: m.month,
                cells: [
                  monthName(m.month),
                  signed(m.new + m.resumed, 1),
                  signed(m.expansion, 1),
                  signed(m.contraction + m.paused, -1),
                  signed(m.churn, -1),
                  <strong key="n" className={`tabular ${m.net > 0 ? 'text-ok' : m.net < 0 ? 'text-danger' : ''}`}>
                    {m.net > 0 ? '+' : m.net < 0 ? '−' : ''}
                    {money(Math.abs(m.net))}
                  </strong>,
                  <span key="m" className="tabular">
                    {money(m.mrr)}
                  </span>,
                  <span key="c" className="tabular">
                    {m.churn_rate ? `${m.churn_rate}%` : '—'}
                  </span>,
                ],
              }))}
            />
            <p className="muted small">{t('sub.results_hint')}</p>
          </>
        )}
      </section>
      {data ? (
        <div className="shop-overview">
          <section className="panel">
            <h2 className="rep-sub">{t('sub.by_plan')}</h2>
            {data.plans.length ? (
              <ul className="shop-ways">
                {data.plans.map((p) => (
                  <li key={p.plan ?? '-'}>
                    <span className="vat-party">
                      <span>{p.plan ?? t('sub.own_terms')}</span>
                      <span className="muted small">{tn('sub.n_active', p.active)}</span>
                    </span>
                    <strong className="tabular">{money(p.mrr)}</strong>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">{t('sub.none_running')}</p>
            )}
          </section>
          <div className="shop-side">
            <section className="panel">
              <h2 className="rep-sub">{t('sub.why_stop')}</h2>
              {data.reasons.length ? (
                <ul className="shop-ways">
                  {data.reasons.map((r) => (
                    <li key={r.reason ?? '-'}>
                      <span className="vat-party">
                        <span>{r.reason ? t(`sub.r_${r.reason}` as MessageKey) : '—'}</span>
                        <span className="muted small">{t('sub.lost_mrr', { amount: money(r.mrr) })}</span>
                      </span>
                      <strong className="tabular">{r.n}</strong>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted">{t('sub.nobody_stopped')}</p>
              )}
            </section>
          </div>
        </div>
      ) : null}
    </div>
  );
}
